/* eslint-disable no-unused-vars */

import {
  Button,
  Card,
  Container,
  LinearProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import React, { ChangeEvent, FC } from 'react';
import { Page } from 'src/components';
import { PurchaseOrderReport } from 'src/redux/slices/purchase-order/types';
import { PurchaseOrderTableRow } from './components/PurchaseOrderTableRow';
import { POReportsToolbar } from './components/POReportsToolbar';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { Pagination } from '@material-ui/lab';
import GetAppIcon from '@material-ui/icons/GetApp';
import { unwrapResult } from '@reduxjs/toolkit';
import { usePermissions, useSnackBar } from 'src/hooks';
import { dateToday, downloadCSV, downloadStringCSV } from 'src/utils';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    minHeight: '90vh'
  }
}));

const {
  actions: purchaseOrderActions,
  selectors: purchaseOrderSelectors
} = slices.purchaseOrder;

export const PurchaseOrderReports: FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();

  const { canDownloadPurchaseOrderReports } = usePermissions();

  const poReports = useAppSelector(purchaseOrderSelectors.selectPOReports);
  const meta = useAppSelector(purchaseOrderSelectors.selectReportsMeta);
  const isLoading = useAppSelector(purchaseOrderSelectors.selectReportsLoading);
  const params = useAppSelector(purchaseOrderSelectors.selectReportsParams);

  const headers: string[] = Object.keys(poReports[0] || {});

  const onChangePage = async (_: any, page: number) => {
    await dispatch(
      purchaseOrderActions.getReportsThunk({ ...params, page: page })
    );
  };

  const onExportToCSV = async () => {
    const res =
      unwrapResult(
        await dispatch(purchaseOrderActions.getReportsCsvThunk(params))
      )?.originalData?.data ?? null;
    if (res) {
      downloadStringCSV(res, `po-reports-${dateToday()}`);
    } else {
      snackbar.show({
        severity: 'error',
        message: 'Failed to get csv, try again later.'
      });
    }
  };

  const PageControls: FC = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'end',
        padding: '1em'
      }}
    >
      <Pagination
        page={meta?.current_page}
        onChange={onChangePage}
        count={meta?.last_page}
      />
    </div>
  );

  return (
    <Page className={classes.root} title="Purchase Order Reports">
      <Container
        maxWidth={false}
        style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}
      >
        <Typography color="textPrimary" gutterBottom variant="h3">
          Purchase Order Reports
        </Typography>

        <POReportsToolbar />

        {isLoading ? <LinearProgress /> : null}
        {!isLoading && poReports?.[0] ? (
          <>
            <PageControls />
            <div style={{ display: 'flex', justifyContent: 'end' }}>
              <Button
                onClick={onExportToCSV}
                startIcon={<GetAppIcon />}
                variant="outlined"
                disabled={!canDownloadPurchaseOrderReports}
              >
                Export as CSV
              </Button>
            </div>
            <Card>
              <Table style={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    {headers.map((header, index) => (
                      <TableCell key={index} align="center">
                        {header.replace(/_/g, ' ').toUpperCase()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {poReports?.map((row, index) => (
                    <PurchaseOrderTableRow
                      key={index}
                      purchaseOrder={row}
                      index={index}
                    />
                  ))}
                </TableBody>
              </Table>
            </Card>

            <PageControls />
          </>
        ) : null}
      </Container>
    </Page>
  );
};

export default PurchaseOrderReports;
